import React from 'react';
import { getVisaFilterOptions, hasActiveVisaFilters, DEFAULT_VISA_FILTERS } from '../utils/visaApplicationFilters';
import { STATUS_FILTER_OPTIONS } from '../utils/visaApplicationStatus';
import CountrySelect from './CountrySelect';

const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm';
const selectClass = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white';
const countryButtonClass =
  'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-left bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent';

const VisaApplicationFilters = ({
  applications,
  filters = DEFAULT_VISA_FILTERS,
  onChange,
  onClear,
  showCompanyFilter = false,
  showManagerFilter = false,
  hideStatusFilter = false,
  managerOptions = [],
  totalCount,
  filteredCount,
}) => {
  const options = getVisaFilterOptions(applications);
  const update = (key, value) => onChange({ ...filters, [key]: value });
  const activeFilters = hasActiveVisaFilters(filters, { ignoreStatus: hideStatusFilter });

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Showing {filteredCount} of {totalCount} applications
          </p>
        </div>
        {activeFilters && (
          <button
            type="button"
            onClick={onClear}
            className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => update('search', e.target.value)}
            className={inputClass}
            placeholder="Passenger ID or applicant name"
          />
        </div>

        {!hideStatusFilter && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => update('status', e.target.value)}
              className={selectClass}
            >
              <option value="">All statuses</option>
              {STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Visa Type</label>
          <select
            value={filters.visaType}
            onChange={(e) => update('visaType', e.target.value)}
            className={selectClass}
          >
            <option value="">All types</option>
            {options.visaTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From Country</label>
          <CountrySelect
            value={filters.fromCountry}
            onChange={(next) => update('fromCountry', next)}
            countries={options.fromCountries}
            placeholder="All from countries"
            emptyLabel="All from countries"
            buttonClassName={countryButtonClass}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To Country</label>
          <CountrySelect
            value={filters.toCountry}
            onChange={(next) => update('toCountry', next)}
            countries={options.toCountries}
            placeholder="All to countries"
            emptyLabel="All to countries"
            buttonClassName={countryButtonClass}
          />
        </div>

        {showCompanyFilter && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Company</label>
            <select
              value={filters.company}
              onChange={(e) => update('company', e.target.value)}
              className={selectClass}
            >
              <option value="">All companies</option>
              {options.companies.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}

        {showManagerFilter && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Manager</label>
            <select
              value={filters.manager}
              onChange={(e) => update('manager', e.target.value)}
              className={selectClass}
            >
              <option value="">All managers</option>
              {managerOptions.map((manager) => (
                <option key={manager.id} value={manager.id}>{manager.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Submitted From</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => update('dateFrom', e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Submitted To</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => update('dateTo', e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
    </div>
  );
};

export default VisaApplicationFilters;
